export type ValidationErrors = Record<string, string[]>;

export type ApiErrorPayload = {
  error?: string;
  errors?: ValidationErrors;
};

type ApiRequestInit = Omit<RequestInit, "body"> & {
  json?: unknown;
};

export class ApiRequestError extends Error {
  constructor(
    readonly status: number,
    readonly payload: unknown,
  ) {
    super(`API request failed with ${status}`);
    this.name = "ApiRequestError";
  }
}

export function getApiErrorPayload(error: unknown): ApiErrorPayload | null {
  if (
    !(error instanceof ApiRequestError) ||
    error.payload === null ||
    typeof error.payload !== "object"
  ) {
    return null;
  }

  return error.payload as ApiErrorPayload;
}

export async function apiRequest<T>(
  input: RequestInfo | URL,
  init: ApiRequestInit = {},
): Promise<T> {
  const { json, ...requestInit } = init;
  const headers = new Headers(requestInit.headers);
  headers.set("Accept", "application/json");

  if (json !== undefined) {
    headers.set("Content-Type", "application/json");
  }

  const response = await fetch(input, {
    cache: "no-store",
    credentials: "same-origin",
    ...requestInit,
    headers,
    body: json === undefined ? undefined : JSON.stringify(json),
  });
  const responseText = await response.text();
  let payload: unknown;

  if (responseText !== "") {
    try {
      payload = JSON.parse(responseText);
    } catch {
      if (response.ok) {
        throw new Error("API returned an invalid JSON response.");
      }
    }
  }

  if (!response.ok) {
    throw new ApiRequestError(response.status, payload);
  }

  return payload as T;
}
